import { useCallback, useEffect, useRef, useState } from 'react';
import { clearCache, getSites, getMe, type Site } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentLocation, isGeolocationSupported, LocationError } from '../lib/teams-integration';

export type LocationState =
  | 'loading'      // Finding location
  | 'blocked'      // Location permission denied by the user
  | 'unsupported'  // No geolocation API available in this host
  | 'unavailable'  // Location lookup failed (timeout / no fix / SDK error)
  | 'no-match'     // Location found but no site within range
  | 'matched'      // Found a site based on location
  | 'static'       // Using user's home site (not geo-based)
  | 'error';       // API or other error

export function useCurrentSite() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [locationState, setLocationState] = useState<LocationState>('loading');
  const [coordinates, setCoordinates] = useState<{ lat: number; lon: number } | null>(null);
  // Distance (km) from the user's detected location to each geo site, keyed by
  // site id. Populated once geolocation succeeds so consumers (e.g. the site
  // selector) can order sites by proximity. Non-geo sites and geo sites without
  // coordinates are simply absent from the map.
  const [siteDistances, setSiteDistances] = useState<Record<string, number>>({});

  // Bumping this re-runs the load effect, which is how refresh() forces a full
  // re-fetch + re-detection without a page reload (e.g. pull-to-refresh).
  const [reloadNonce, setReloadNonce] = useState(0);

  // Guards against stale async work: each load run captures the current token
  // and bails if a newer run (or unmount) has superseded it.
  const runToken = useRef(0);

  /**
   * Re-run site loading and location detection from scratch. Clears the API
   * cache first so sites and the user profile are re-fetched, then triggers the
   * load effect. Used by pull-to-refresh and any explicit "refresh" affordance.
   */
  const refresh = useCallback(() => {
    if (!isAuthenticated) return;
    clearCache();
    setReloadNonce(n => n + 1);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      clearCache();
      setSites([]);
      setSite(null);
      setError('');
      setCoordinates(null);
      setSiteDistances({});
      setLocationState('static');
      setLoading(false);
      return;
    }

    const token = ++runToken.current;
    const isRunActive = () => token === runToken.current;

    (async () => {
      try {
        setLoading(true);
        setError('');
        setLocationState('loading');
        setCoordinates(null);
        setSiteDistances({});

        // **CRITICAL OPTIMIZATION: Load sites first, show them ASAP**
        const sitesPromise = getSites();
        const mePromise = getMe().catch(() => null);
        
        // Don't wait for both - show sites as soon as they arrive
        const s = await sitesPromise;
        if (!isRunActive()) {
          return;
        }
        setSites(s);
        
        // **INSTANT UI: Show first site immediately (0ms delay)**
        const fallbackSite = s[0] ?? null;
        setSite(fallbackSite);
        setLoading(false); // Unlock UI immediately - buttons appear now!
        
        // Load user preferences in background (non-blocking)
        const me = await mePromise;
        if (!isRunActive()) {
          return;
        }
        
        // Prefer user's home site if set (will update UI if different from fallback)
        let selected: Site | null = null;
        if (me?.homeSiteId) {
          selected = s.find(x => x.id === me.homeSiteId) ?? null;
          if (selected) {
            setSite(selected);
            setLocationState('static');
            return;
          }
        }
        
        // Try geolocation to pick nearest geo site within radius (non-blocking).
        // getCurrentLocation() routes through the Teams geoLocation API when
        // running inside Teams (which requires the manifest devicePermission),
        // and falls back to navigator.geolocation in a normal browser.
        const geoSites = s.filter(x => x.mode === 'geo' && typeof x.lat === 'number' && typeof x.lon === 'number' && (x.radiusKm ?? 0) > 0);
        if (geoSites.length) {
          if (isGeolocationSupported()) {
            try {
              const { latitude, longitude } = await getCurrentLocation();
              if (!isRunActive()) {
                return;
              }
              setCoordinates({ lat: latitude, lon: longitude });
              
              function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number) {
                const R = 6371;
                const dLat = (bLat - aLat) * Math.PI / 180;
                const dLon = (bLon - aLon) * Math.PI / 180;
                const ra = aLat * Math.PI / 180;
                const rb = bLat * Math.PI / 180;
                const h = Math.sin(dLat / 2) ** 2 + Math.cos(ra) * Math.cos(rb) * Math.sin(dLon / 2) ** 2;
                return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
              }
              
              const ranked = geoSites
                .map(gs => ({ gs, d: haversineKm(latitude, longitude, gs.lat!, gs.lon!) }))
                .sort((a, b) => a.d - b.d);

              // Expose every geo site's distance (not just in-range ones) so the
              // selector can order the full list by proximity to the user.
              setSiteDistances(Object.fromEntries(ranked.map(x => [x.gs.id, x.d])));

              const within = ranked.filter(x => x.d <= (x.gs.radiusKm ?? 0));

              if (within[0]) {
                selected = within[0].gs;
                setSite(selected); // Update to geo-matched site
                setLocationState('matched');
              } else {
                setLocationState('no-match');
              }
            } catch (geoError) {
              if (!isRunActive()) {
                return;
              }
              // Map the specific failure reason onto a location state so the UI
              // (and any diagnostics) can distinguish a permission denial from an
              // unsupported host or a transient lookup failure, instead of
              // collapsing everything to "blocked". Keep the fallback site that
              // was already set in all cases.
              const reason = geoError instanceof LocationError ? geoError.reason : 'sdk-error';
              if (reason === 'denied') {
                setLocationState('blocked');
              } else if (reason === 'unsupported') {
                setLocationState('unsupported');
              } else {
                // 'timeout' | 'unavailable' | 'sdk-error'
                setLocationState('unavailable');
              }
            }
          } else {
            setLocationState('unsupported');
          }
        } else {
          // No geo sites configured, stay with fallback
          setLocationState('static');
        }
      } catch (e: any) {
        if (!isRunActive()) {
          return;
        }
        setError(e?.message || String(e));
        setLocationState('error');
        setLoading(false);
      }
    })();

    return () => {
      // Supersede this run so any in-flight async work bails out.
      runToken.current++;
    };
  }, [isAuthenticated, isAuthLoading, reloadNonce]);

  return {
    sites,
    site,
    setSite,
    loading,
    error,
    locationState,
    coordinates,
    siteDistances,
    refresh
  };
}
